/* eslint-disable no-unsafe-optional-chaining */
import React, { useState, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useQuery, useMutation } from '@apollo/client'
import cn from 'classnames'
// Components
import HeadSeo from '@/components/HeadSeo'
import CitiesList from '@/components/landings/CitiesList'
import Footer from '@/components/newFooter'
import Button from '@/components/newButton/Button'
import OurPartners from '@/components/ourPartners'
// Utils
import { initializeApollo } from '@/utils/apolloClient'
import { getToken, getUserData } from '@/utils/tokenStorage'
import useMediaQuery from '@/utils/publicApi'
import { stateMap } from '@/utils/states'
import {
  breedNameTextConverter,
  capitalizeFirstLetterOfTitle,
  slugReverter,
} from '@/utils/converter'

import useRelatedBreeds from '@/hooks/useRelatedBreeds'
import { DogsLandingsContext } from '@context/dogsLandingsContext'

import styles from './index.module.scss'
import cities from '@/utils/us-cities.json'
// GQL Queries
import {
  GET_FRONTEND_CONFIG_LANDING,
  GET_CATEGORIES,
  GET_STATE_LANDING,
  GET_LANDINGS_BREED_DETAILS,
  PUPPY_SEARCH,
  GET_CUSTOMER_FAVOURITES,
  UPDATE_FAVOURITE,
  BREED_LIST_BY_STATE,
  STATE_LIST_BY_BREED,
} from '@/graphql/index'

// Dynamic
const CustomerReview = dynamic(
  () => import('@/components/search/CustomerReview'),
)
const Featured = dynamic(() => import('@/components/landings/Featured'))
const LandingCover = dynamic(() => import('@/components/landings/LandingCover'))
const How = dynamic(() => import('@/components/home/How'))
const Why = dynamic(() => import('@/components//home/Why'))
const Facts = dynamic(() => import('@/components/landings/Facts'))
const RelatedBreeds = dynamic(
  () => import('@/components/landings/RelatedBreeds'),
)
const Categories = dynamic(() => import('@/components/home/Categories'))
const NationWide = dynamic(() => import('@/components/landings/NationWide'))
const RelatedBreedsDogs = dynamic(
  () => import('@/components/search/RelatedBreeds'),
)
const StateHistory = dynamic(() => import('@/components/landings/StateHistory'))
const PuppyQuizBanner = dynamic(
  () => import('@/components/search/PuppyQuizBanner'),
)
const SignupModal = dynamic(() => import('@/components/signupModal'))
const Header = dynamic(() => import('@/components/newHeader/Header'))
const BreedsList = dynamic(() => import('@/components/landings/BreedList'))

export default function BreedCityLanding({
  breedsByState,
  statesByBreed,
  nationwidePuppies,
  allBreedPuppies,
  breedDetails,
  statesData,
  categoriesData,
  variables,
  landingType,
  citySlug,
}) {
  const [showSignup, setShowSignup] = useState(false)
  const [selectedDog, setSelectedDog] = useState(null)
  const [userData, setUserData] = useState(getUserData(getToken()))

  const isMobile = useMediaQuery('(max-width:600px)')
  const router = useRouter()
  const breed = breedDetails?.breed || {}

  const { relatedBreeds } = useRelatedBreeds(breed?.id)
  const nationWideDogs = useMemo(
    () => nationwidePuppies?.search?.rows || [],
    [nationwidePuppies],
  )

  const breedPuppies = useMemo(() => {
    const puppies = allBreedPuppies?.search?.rows.filter((elem) => {
      return elem.breeder.state === variables?.state
    })

    // Sort puppies by distance
    puppies.sort((a, b) => a.distance - b.distance)

    return puppies
  }, [allBreedPuppies, variables?.state])

  const dogs = breedPuppies || []
  // handle state not found or timeout error
  const stateFacts =
    statesData?.state && statesData?.state?.length && statesData.state[0]
      ? statesData.state[0]
      : {}

  // Memoize the relatedBreedsDogs array
  const relatedBreedsDogs = useMemo(() => {
    return relatedBreeds
      .map((breed) => ({
        id: breed?.id,
        name: breed?.name,
        slug: breed?.slug,
        dogs: [...breed?.dogs].filter((dog) =>
          [...variables?.borderingStates, variables?.state]?.includes(
            dog?.breeder?.state,
          ),
        ),
        dogsAvailable: [...breed?.dogs].filter((dog) =>
          [...variables?.borderingStates, variables?.state]?.includes(
            dog?.breeder?.state,
          ),
        ).length,
      }))
      .filter((breed) => breed?.dogs?.length > 0)
  }, [relatedBreeds, variables?.borderingStates, variables?.state])

  // Filter the breeds that doesn't have any dogs
  const filteredBreedDogs = relatedBreedsDogs?.filter(
    (breed) => breed?.dogs?.length > 0,
  )

  const { data: favouriteData } = useQuery(GET_CUSTOMER_FAVOURITES, {
    skip: !userData,
  })

  const [updateFavourite] = useMutation(UPDATE_FAVOURITE, {
    refetchQueries: [{ query: GET_CUSTOMER_FAVOURITES }],
    onError: (error) => {
      setShowSignup(true)
      console.error('updateFavourite', error)
    },
  })

  const handleUpdateFavourite = (id) => {
    if (userData) {
      updateFavourite({ variables: { dog: id } })
    } else {
      setShowSignup(true)
      setSelectedDog(id)
    }
  }

  return (
    <DogsLandingsContext.Provider value={{ variables, landingType }}>
      <HeadSeo
        breed={breed}
        variables={variables}
        landingType={landingType}
        citySlug={citySlug}
      />
      <SignupModal
        title={undefined}
        subtitle={undefined}
        isOpen={showSignup}
        close={() => setShowSignup(false)}
        success={() => {
          setShowSignup(false)
          setUserData(getUserData(getToken()))
          updateFavourite({
            variables: { dog: selectedDog },
          })
        }}
        successRedirect={undefined}
      />
      <div className={styles.main}>
        <Header />
        <LandingCover
          breed={breed}
          variables={variables}
          landingType={landingType}
          city={capitalizeFirstLetterOfTitle(slugReverter(citySlug))}
        />
        <Featured
          title={`Our ${breedNameTextConverter(
            breed?.name,
          )} Puppies in ${capitalizeFirstLetterOfTitle(
            slugReverter(citySlug),
          )}, ${variables.state}`}
          dogs={dogs}
          variables={variables}
          breed={breed}
          city={capitalizeFirstLetterOfTitle(slugReverter(citySlug))}
        />

        {nationWideDogs.length > 0 && (
          <NationWide
            title={` ${breedNameTextConverter(breed?.name)} Puppies Nationwide`}
            allDogs={nationWideDogs}
            slug={breed?.slug}
            hasBreeds
          />
        )}

        {filteredBreedDogs.length !== 0 && (
          <div
            className={cn(styles._sectionRbdogs, styles.relatedBreedsWrapper)}
            id="relatedDogBreeds"
          >
            <RelatedBreedsDogs
              title="Related Breeds Nearby"
              relatedBreeds={filteredBreedDogs}
              favourites={favouriteData?.customer?.favourites || []}
              updateFavourite={handleUpdateFavourite}
              state
            />
            <div className={styles.featuredButton}>
              <Button
                title={`Explore puppies in ${variables?.stateName}`}
                theme={['green-solid', 'medium']}
                rightImage="/next-white.svg"
                onClick={() =>
                  router.push(`/puppies-for-sale/${variables?.stateSlug}`)
                }
              />
            </div>
          </div>
        )}

        <div className={styles._sectionRw}>
          {isMobile && <hr />}
          <CustomerReview />
        </div>
        <How />
        <Why />
        <div className={styles._sectionQuiz}>
          <OurPartners />
          <PuppyQuizBanner />
        </div>

        <Facts breed={breed} />
        <div id="relatedBreeds" className={styles._sectionRb}>
          <RelatedBreeds relatedBreeds={relatedBreeds} breed={breed} />
        </div>
        <div className={styles._sectionCategories}>
          <Categories
            title="Explore dog categories nationwide"
            categories={Object.keys(categoriesData).filter(
              (c) => c !== '__typename',
            )}
          />
        </div>

        {stateFacts?.history && stateFacts?.facts && (
          <StateHistory state={stateFacts} variables={variables} />
        )}

        <div className={styles.listWrapper}>
          <CitiesList
            type="breed-state"
            title={`Find ${breed.name}s in Nearby Cities`}
            breedName={breed?.slug}
            more="View all cities"
            less="View less cities"
          />
          <BreedsList
            type="byLocation"
            more="View all locations"
            less="View less locations"
            title={`Find ${breed?.name}s in Other States`}
            breedSlug={breed?.slug}
            breedsData={statesByBreed}
            stateName={variables?.stateName}
            stateSlug={variables?.stateSlug}
            withDogs={statesByBreed?.withDogs}
            withoutDogs={statesByBreed?.withoutDogs}
          />
          <BreedsList
            type="inState"
            more="View all breeds"
            less="View less breeds"
            title={`Find Other Breeds in ${variables?.stateName}`}
            breedSlug={breed?.slug}
            breedsData={breedsByState}
            stateName={variables?.stateName}
            stateSlug={variables?.stateSlug}
            withDogs={breedsByState?.withDogs}
            withoutDogs={breedsByState?.withoutDogs}
          />
        </div>

        <Footer />
      </div>
    </DogsLandingsContext.Provider>
  )
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

// Redirection map
const redirects = {
  'weimaraner-1': 'weimaraner',
  cavadoodles: 'cavapoo',
  'golden-bernedoodle': 'golden-mountain-doodle',
  'central-asian-shepherd-dog-1': 'central-asian-shepherd-dog',
  'papi-tzu': 'papastzu',
  'Yorkie-Bichon-Mix': 'yorkie-bichon-mix',
  poodle: 'standard-poodle',
}

export async function getStaticProps({ params }) {
  const apolloClient = initializeApollo()
  const slug = params['breed-slug']
  const stateSlug = params?.state
  const citySlug = params?.city

  let variables
  let landingType = ''

  let categoriesData = {}
  let breedDetails = {
    breed: null,
  }
  let statesData = {}
  let allBreedPuppies
  let nationwidePuppies

  let breedsByState = {
    withDogs: [],
    withoutDogs: [],
    breedListByState: [],
  }
  let statesByBreed = {
    withDogs: [],
    withoutDogs: [],
    stateListByBreed: [],
  }

  if (slug && stateSlug && citySlug) {
    // Check if the breed slug is in the redirection map
    landingType = 'breedCity'

    const city = cities.find((city) => city.slug === citySlug)
    if (!city) return { notFound: true }

    if (Object.prototype.hasOwnProperty.call(stateMap, stateSlug)) {
      variables = {
        // breed slug
        slug,
        // state data
        state: stateMap[stateSlug].state,
        stateName: stateMap[stateSlug].name,
        country: 'USA',
        stateSlug,
        borderingStates: stateMap[stateSlug].borderingState,
        cityZipcode: city['zip code'],
      }
    } else {
      if (stateSlug === 'samoa') {
        return {
          redirect: {
            destination: `/puppies-for-sale/${slug}/american-samoa`,
            statusCode: 301,
          },
        }
      } else {
        return {
          notFound: true,
        }
      }
    }

    // Check for redirects
    if (redirects[slug]) {
      return {
        redirect: {
          destination: `/puppies-for-sale/${redirects[slug]}/${stateSlug}`,
          statusCode: 301,
        },
      }
    }
  }
  try {
    const { data } = await apolloClient.query({
      fetchPolicy: 'no-cache',
      query: GET_LANDINGS_BREED_DETAILS,
      variables: {
        slug: variables?.slug,
      },
    })

    breedDetails = data

    const {
      data: { frontendConfig: _landingPhotos },
    } = await apolloClient.query({
      query: GET_FRONTEND_CONFIG_LANDING,
      variables: {
        key: `breed-${data?.breed?.id}`,
      },
    })

    const landingPhotos = _landingPhotos?.value

    const { data: _breedPuppies } = await apolloClient.query({
      fetchPolicy: 'no-cache',
      query: PUPPY_SEARCH,
      variables: {
        breedSlug: variables?.slug,
        state: variables?.state,
        sort: 'by-view-score',
        frontendConfigData: landingPhotos || '[]',
        limit: 20,
        zipcode: variables?.cityZipcode.toString() || '',
      },
    })

    allBreedPuppies = _breedPuppies

    // Nationwide

    const { data: _allPuppies } = await apolloClient.query({
      fetchPolicy: 'no-cache',
      query: PUPPY_SEARCH,
      variables: {
        breedSlug: variables?.slug,
        limit: 5,
        page: 1,
      },
    })

    nationwidePuppies = _allPuppies

    // Categories

    const {
      data: { categories },
    } = await apolloClient.query({ query: GET_CATEGORIES })
    categoriesData = categories

    // States

    const { data: _stateData } = await apolloClient.query({
      query: GET_STATE_LANDING,
      variables: { code: variables?.state },
    })
    statesData = _stateData

    // Data List

    const { data: breedsByStateList } = await apolloClient.query({
      query: BREED_LIST_BY_STATE,
      variables: { state: variables?.state },
    })
    breedsByState = breedsByStateList

    const { data: statesByBreedList } = await apolloClient.query({
      query: STATE_LIST_BY_BREED,
      variables: { slug: variables?.slug },
    })
    statesByBreed = statesByBreedList
  } catch (error) {
    console.error('BREED + STATE LANDING ERROR', error)
  }

  if (breedDetails?.breed === null) {
    return {
      notFound: true,
    }
  }
  return {
    props: {
      breedsByState: breedsByState?.breedListByState || {},
      statesByBreed: statesByBreed?.stateListByBreed || {},
      allBreedPuppies: allBreedPuppies || {},
      nationwidePuppies: nationwidePuppies || {},
      breedDetails: breedDetails || {},
      categoriesData: categoriesData || {},
      statesData: statesData || {},
      landingType: landingType || '',
      variables: variables || {},
      citySlug: citySlug || '',
    },
    revalidate: 60 * 60 * 72, // Revalidate every 3 days
  }
}
